import keyBy from 'lodash/keyBy';

import { Account, AccountGroupProfile, Allocation, Conviction, GUID } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { useAllocations } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { AccountGroupProfileWithAumIncluded, AccountWithAumIncluded } from 'views/portfolio/account-aum-queries';

import { ExtendedConviction, LifestyleProtectionInfo } from './types';

export type AccountGroupAllocations = {
    allocations: Allocation[];
    portfolioAllocation?: Allocation;
    accountAllocations: Record<GUID<Account>, Allocation | undefined>;
    isPending: boolean;
    isLoadingError: boolean;
    isError: boolean;
    refetch: () => Promise<unknown>;
};

export const useAccountGroupAllocations = (
    accountGroupProfile?: AccountGroupProfile,
    options?: { enabled?: boolean; throwOnError?: boolean },
): AccountGroupAllocations => {
    const portfolioAllocationGUID = accountGroupProfile?.accountGroup.allocationGUID;
    const allocationGUIDs = portfolioAllocationGUID
        ? [portfolioAllocationGUID]
        : accountGroupProfile?.accounts.flatMap((account) => account.allocationGUID ?? []);

    const {
        data: allocations = [],
        isPending,
        isLoadingError,
        isError,
        refetch,
    } = useAllocations(allocationGUIDs ?? [], options);
    const allocationMap = keyBy(allocations, (allocation) => allocation.allocationGUID);

    const portfolioAllocation = allocationMap[portfolioAllocationGUID ?? ''];
    const accountAllocations = Object.fromEntries(
        accountGroupProfile?.accounts.map((account) => [
            account.accountGUID,
            allocationMap[account.allocationGUID ?? ''],
        ]) ?? [],
    );

    return {
        allocations,
        portfolioAllocation,
        accountAllocations,
        isPending,
        isLoadingError,
        isError,
        refetch,
    };
};

export const getLifestyleProtectionInfo = (
    accountGroupProfile: AccountGroupProfileWithAumIncluded | undefined,
): LifestyleProtectionInfo | undefined => {
    const securedAccount = accountGroupProfile?.accounts?.find(
        (account: AccountWithAumIncluded) => account.initialSecuredness !== '0',
    );

    if (securedAccount) {
        return {
            initialSecuredness: parseFloat(securedAccount.initialSecuredness),
            pinnedAccountGUID: securedAccount.accountGUID,
            effectiveTaxRate: parseFloat(securedAccount.estimatedTaxRate ?? ''),
            targetEndDate: accountGroupProfile?.accountGroup.targetEndDate,
        };
    }
};

export const getConvictions = (
    accountGroupProfile: AccountGroupProfileWithAumIncluded | undefined,
    stocksMap: Map<string, AssetInfo>,
): Record<string, ExtendedConviction[]> => {
    return (
        accountGroupProfile?.accounts?.reduce(
            (acc: Record<string, ExtendedConviction[]>, account: AccountWithAumIncluded) => {
                const convictions: ExtendedConviction[] =
                    account.convictions?.map((conviction: Conviction) => ({
                        ...conviction,
                        ticker: stocksMap.get(conviction.securityId)?.symbol || '',
                        account: account.accountName.plainText,
                    })) || [];
                if (convictions.length > 0) {
                    acc[account.accountGUID] = convictions;
                }
                return acc;
            },
            {},
        ) ?? {}
    );
};
